// US-11 — Home rediseñada (UC-11.05) + US-16 KPIs proyectos (UC-16.05)
// US-21 / UC-21.04 — eliminado mock hardcoded + cableado a hooks reales.
// US-25 / UC-25.04 — FiscalProvisionTile: provisión IRPF del trimestre en curso.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Bike, ChevronRight, CheckCircle2 } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  KPIStat,
  Sheet,
  StatusPill,
  type DataTableColumn,
} from "@/components/ui/v2";
import { Amount } from "@/components/masked/Amount";
import { MaskedClient } from "@/components/masked/MaskedClient";
import { formatCurrency, formatIsoDate, getCurrentQuarter } from "@/lib/format";
import { formatEventHint, formatEventTime, formatRelativeTime } from "@/lib/calendar-format";
import { FiscalProvisionTile } from "@/components/widgets/FiscalProvisionTile";
import { useProjects } from "@/hooks/useProjects";
import { useWeeklyCapacity } from "@/hooks/useWeeklyCapacity";
import { useInvoices } from "@/hooks/useInvoices";
import { useBillingSummary } from "@/hooks/useBillingSummary";
import { useMonthlyRevenue } from "@/hooks/useMonthlyRevenue";
import { useTaxSummary } from "@/hooks/useTaxSummary";
import { useWeekEvents } from "@/hooks/useWeekEvents";
import { useTopClientsYTD } from "@/hooks/useTopClientsYTD";
import { useUnreadEmailsList } from "@/hooks/useUnreadEmailsList";
import { calendarColor } from "@/lib/calendar-types";
import type { Invoice, InvoiceState } from "@/types/billing";
import type { CalendarEvent } from "@/lib/calendar-types";

function greetingByHour(now: Date = new Date()): string {
  const h = now.getHours();
  if (h < 13) return "Buenos días";
  if (h < 21) return "Buenas tardes";
  return "Buenas noches";
}

function fullDateLine(now: Date = new Date()): string {
  const longDate = now.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const { quarter, year } = getCurrentQuarter(now);
  return `Hoy es ${longDate} · Q${quarter} ${year} en curso`;
}

interface InvoiceRow {
  id: string;
  number: string;
  client: string;
  date: string;
  amount: number;
  status: "paid" | "pending" | "overdue" | "draft";
}

function mapInvoiceState(state: InvoiceState): InvoiceRow["status"] {
  switch (state) {
    case "pagada":
      return "paid";
    case "emitida":
      return "pending";
    case "borrador":
      return "draft";
    case "anulada":
    case "rectificativa":
      return "overdue";
  }
}

function mapInvoiceToRow(inv: Invoice): InvoiceRow {
  return {
    id: String(inv.id),
    number: inv.invoice_number,
    client: inv.customer_name,
    date: formatIsoDate(inv.invoice_date),
    amount: inv.sum_total,
    status: mapInvoiceState(inv.state),
  };
}

function InvoiceSheetBody({ invoice }: { invoice: InvoiceRow }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-xs uppercase text-fg-secondary mb-1">Número</p>
        <p className="text-md font-medium tabular font-mono">{invoice.number}</p>
      </div>
      <div>
        <p className="text-xs uppercase text-fg-secondary mb-1">Cliente</p>
        <p className="text-md font-medium">
          <MaskedClient value={invoice.client} />
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase text-fg-secondary mb-1">Fecha</p>
          <p className="text-sm tabular font-mono">{invoice.date}</p>
        </div>
        <div>
          <p className="text-xs uppercase text-fg-secondary mb-1">Importe</p>
          <p className="text-sm tabular font-mono text-fg">
            <Amount value={invoice.amount} />
          </p>
        </div>
      </div>
      <div>
        <p className="text-xs uppercase text-fg-secondary mb-1">Estado</p>
        <StatusPill status={invoice.status} />
      </div>
      <p className="text-sm text-fg-secondary mt-2">
        Esto es una vista previa rápida. Para detalle completo abre la factura desde Facturación.
      </p>
    </div>
  );
}

function pluralFacturas(n: number): string {
  return `${n} factura${n === 1 ? "" : "s"}`;
}

function HomePage() {
  const today = useMemo(() => new Date(), []);
  const greeting = greetingByHour(today);
  const dateLine = fullDateLine(today);
  const [openInvoice, setOpenInvoice] = useState<InvoiceRow | null>(null);

  const { items: projects } = useProjects();
  const { capacity } = useWeeklyCapacity();

  // ---- Data hooks ----------------------------------------------------------
  const invoicesQ = useInvoices({ status: undefined });
  const billingSummaryQ = useBillingSummary();
  const monthlyRevenueQ = useMonthlyRevenue();
  const tax303Q = useTaxSummary({ model: 303 });
  const weekEventsQ = useWeekEvents(today);
  const topClientsQ = useTopClientsYTD(5);
  const emailsQ = useUnreadEmailsList("all");

  // ---- Derivations ---------------------------------------------------------
  const projectStats = useMemo(() => {
    const active = projects.filter((p) => p.commercial_status === "active");
    const totalHours = active.reduce((sum, p) => sum + Number(p.hours_per_week ?? 0), 0);
    const pct = capacity > 0 ? Math.round((totalHours / capacity) * 100) : 0;
    const upcoming = [...active]
      .filter((p) => !!p.business_end_date)
      .sort((a, b) => (a.business_end_date ?? "").localeCompare(b.business_end_date ?? ""))[0];
    return { activeCount: active.length, totalHours, pct, upcoming };
  }, [projects, capacity]);

  const recentInvoiceRows = useMemo<InvoiceRow[]>(
    () => invoicesQ.items.slice(0, 5).map(mapInvoiceToRow),
    [invoicesQ.items],
  );

  const pendingStats = useMemo(() => {
    const pending = invoicesQ.items.filter(
      (inv) => inv.state === "emitida" && inv.paid_at === null,
    );
    return {
      total: pending.reduce((s, inv) => s + inv.sum_total, 0),
      count: pending.length,
    };
  }, [invoicesQ.items]);

  const billedHistorical = useMemo(
    () => (monthlyRevenueQ.data ?? []).map((p) => p.total),
    [monthlyRevenueQ.data],
  );

  const momDelta = useMemo(() => {
    const s = billingSummaryQ.data;
    if (!s || s.prevMonth.total === 0) return null;
    const pct = Math.round(((s.thisMonth.total - s.prevMonth.total) / s.prevMonth.total) * 100);
    return pct;
  }, [billingSummaryQ.data]);

  const now = today;
  const upcomingFive = useMemo<CalendarEvent[]>(() => {
    const sorted = [...weekEventsQ.items].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return sorted.filter((e) => new Date(e.start_time) >= now).slice(0, 5);
  }, [weekEventsQ.items, now]);
  const nextEvent = upcomingFive[0];

  const topClientsList = topClientsQ.data?.items ?? [];

  const unreadEmails = useMemo(
    () => emailsQ.items.filter((e) => e.unread).slice(0, 3),
    [emailsQ.items],
  );
  const unreadCount = emailsQ.total;

  // ---- Columns -------------------------------------------------------------
  const invoiceColumns: DataTableColumn<InvoiceRow>[] = [
    { key: "number", header: "Nº Factura", accessor: (r) => r.number, mono: true, width: "140px" },
    {
      key: "client",
      header: "Cliente",
      accessor: (r) => <MaskedClient value={r.client} />,
    },
    { key: "date", header: "Fecha", accessor: (r) => r.date, mono: true, width: "120px" },
    {
      key: "amount",
      header: "Importe",
      accessor: (r) => <Amount value={r.amount} />,
      align: "right",
      mono: true,
      width: "120px",
    },
  ];

  // ---- KPI values ----------------------------------------------------------
  const facturadoMtdValue = billingSummaryQ.isLoading ? (
    "—"
  ) : (
    <Amount value={billingSummaryQ.data?.thisMonth.total ?? 0} />
  );

  const facturadoMtdDelta = momDelta === null ? undefined : { value: momDelta, suffix: "MoM" };

  const pendingValue = invoicesQ.isLoading ? "—" : <Amount value={pendingStats.total} />;

  const tax303 = tax303Q.data?.model === 303 ? tax303Q.data : undefined;
  const quarterLabel = tax303 ? `Q${tax303.quarter} IVA cuota` : "IVA cuota";
  const quarterValue = tax303 ? <Amount value={tax303.cuota} /> : "—";
  const quarterSubRows = tax303
    ? [
        { label: "IVA repercutido", value: <Amount value={tax303.ivaRepercutido} /> },
        { label: "IVA soportado", value: <Amount value={tax303.ivaSoportado} /> },
        {
          label: tax303.cuota >= 0 ? "Cuota positiva" : "Cuota negativa",
          value: "→",
          positive: tax303.cuota >= 0,
        },
      ]
    : undefined;

  const nextEventValue = nextEvent ? nextEvent.title : "Sin eventos próximos";
  const nextEventHint = nextEvent
    ? formatEventHint(nextEvent.start_time, nextEvent.end_time, now)
    : "—";

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-lg font-semibold tracking-tight text-fg">{greeting}, Jesús</h1>
        <p className="text-sm text-fg-secondary mt-1 tabular">{dateLine}</p>
      </header>

      {/* KPI Strip */}
      <section className="grid grid-cols-12 gap-3" aria-label="KPIs principales">
        <div className="col-span-3">
          <KPIStat
            label="Facturado MTD"
            value={facturadoMtdValue}
            delta={facturadoMtdDelta}
            sparklineData={billedHistorical}
          />
        </div>
        <div className="col-span-3">
          <KPIStat
            label="Pendiente cobro"
            value={pendingValue}
            hint={pluralFacturas(pendingStats.count)}
          />
        </div>
        <div className="col-span-3">
          <KPIStat
            label={quarterLabel}
            value={quarterValue}
            subRows={quarterSubRows}
            footer={
              <Link
                to="/billing"
                className="inline-flex items-center gap-1 text-accent hover:underline"
              >
                Ver modelo 303 <ChevronRight className="size-3" strokeWidth={1.5} />
              </Link>
            }
          />
        </div>
        <div className="col-span-3">
          <KPIStat label="Próximo evento" value={nextEventValue} hint={nextEventHint} />
        </div>
      </section>

      {/* US-16 — Proyectos KPIs */}
      <section className="grid grid-cols-12 gap-3" aria-label="Proyectos">
        <div className="col-span-4">
          <KPIStat
            label="Proyectos activos"
            value={projectStats.activeCount.toString()}
            hint={
              projectStats.activeCount === 1
                ? "1 proyecto en curso"
                : `${projectStats.activeCount} proyectos en curso`
            }
            footer={
              <Link
                to="/projects"
                className="inline-flex items-center gap-1 text-accent hover:underline"
              >
                Ver proyectos <ChevronRight className="size-3" strokeWidth={1.5} />
              </Link>
            }
          />
        </div>
        <div className="col-span-4">
          <KPIStat
            label="Carga semanal"
            value={`${projectStats.totalHours.toFixed(1)}h / ${capacity}h`}
            delta={
              projectStats.totalHours === 0
                ? undefined
                : { value: projectStats.pct - 100, suffix: "vs capacidad" }
            }
            hint={
              projectStats.totalHours > capacity
                ? `Sobre capacidad por ${(projectStats.totalHours - capacity).toFixed(1)}h`
                : `${projectStats.pct}% de tu capacidad`
            }
          />
        </div>
        <div className="col-span-4">
          <KPIStat
            label="Próximo cierre"
            value={projectStats.upcoming ? projectStats.upcoming.name : "—"}
            hint={
              projectStats.upcoming?.business_end_date
                ? `Estimado ${projectStats.upcoming.business_end_date}`
                : "Sin fechas de cierre estimadas"
            }
          />
        </div>
      </section>

      {/* US-25 UC-25.04 — Provisión IRPF del trimestre en curso */}
      <section className="grid grid-cols-12 gap-3" aria-label="Posición IRPF">
        <div className="col-span-4">
          <FiscalProvisionTile />
        </div>
      </section>

      {/* Row A — Facturas recientes + Próximos eventos */}
      <section className="grid grid-cols-12 gap-4">
        <Card className="col-span-8">
          <CardHeader>
            <CardTitle>Facturas recientes</CardTitle>
            <Link to="/billing" className="text-sm text-accent hover:underline">
              Ver todas →
            </Link>
          </CardHeader>
          {invoicesQ.isLoading ? (
            <p className="text-sm text-fg-tertiary py-4">Cargando…</p>
          ) : invoicesQ.error ? (
            <p className="text-sm text-fg-secondary py-4">No se pudieron cargar las facturas.</p>
          ) : recentInvoiceRows.length === 0 ? (
            <EmptyState
              padding="md"
              title="Sin facturas recientes"
              description="Cuando emitas o sincronices facturas aparecerán aquí."
            />
          ) : (
            <DataTable
              ariaLabel="Facturas recientes"
              columns={invoiceColumns}
              data={recentInvoiceRows}
              rowKey={(r) => r.id}
              onRowClick={(r) => setOpenInvoice(r)}
              stickyHeader={false}
            />
          )}
        </Card>

        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Próximos eventos</CardTitle>
            <span className="text-xs text-fg-secondary">Esta semana</span>
          </CardHeader>
          {weekEventsQ.isLoading ? (
            <p className="text-sm text-fg-tertiary py-3">Cargando…</p>
          ) : weekEventsQ.error ? (
            <p className="text-sm text-fg-secondary py-3">No se pudo cargar el calendario.</p>
          ) : upcomingFive.length === 0 ? (
            <EmptyState padding="md" title="Sin eventos esta semana" />
          ) : (
            <ul className="flex flex-col">
              {upcomingFive.map((event) => (
                <li
                  key={event.id}
                  className="flex items-center gap-2 py-2 border-b border-default last:border-b-0"
                >
                  <span
                    className="inline-block size-2 rounded-full shrink-0"
                    style={{ backgroundColor: calendarColor(event.calendar_id) }}
                    aria-hidden="true"
                  />
                  <span className="flex-1 text-sm text-fg truncate">{event.title}</span>
                  <span className="text-xs text-fg-secondary tabular font-mono shrink-0">
                    {formatEventTime(event.start_time, now)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/calendar" className="block mt-3 text-sm text-accent hover:underline">
            Ver agenda completa →
          </Link>
        </Card>
      </section>

      {/* Row B — Top clients YTD + Email triage */}
      <section className="grid grid-cols-12 gap-4">
        <Card className="col-span-6">
          <CardHeader>
            <CardTitle>Top 5 clientes YTD</CardTitle>
            <span className="text-xs text-fg-secondary">{new Date().getFullYear()}</span>
          </CardHeader>
          {topClientsQ.isLoading ? (
            <p className="text-sm text-fg-tertiary py-3">Cargando…</p>
          ) : topClientsList.length === 0 ? (
            <EmptyState
              padding="md"
              title="Sin facturas este año"
              description="Cuando factures, aparecerán los top clientes."
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {topClientsList.map((client) => (
                <li key={client.name} className="flex items-center gap-3">
                  <span className="text-sm text-fg flex-1 min-w-0 truncate">
                    <MaskedClient value={client.name} />
                  </span>
                  <div className="flex-1 h-2 rounded-full bg-subtle overflow-hidden">
                    <div
                      className="h-full bg-accent rounded-full transition-all"
                      style={{ width: `${Math.round(client.percent)}%` }}
                    />
                  </div>
                  <span className="text-sm tabular font-mono text-fg w-24 text-right">
                    <Amount value={client.total} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="col-span-6">
          <CardHeader>
            <CardTitle>
              Email triage{" "}
              {unreadCount > 0 ? (
                <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium bg-accent-soft text-accent px-2 py-0.5 rounded-sm">
                  {unreadCount} sin leer
                </span>
              ) : (
                <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium bg-success-soft text-success px-2 py-0.5 rounded-sm">
                  <CheckCircle2 className="size-3" strokeWidth={1.5} aria-hidden />
                  Bandeja al día
                </span>
              )}
            </CardTitle>
            <Link to="/email" className="text-sm text-accent hover:underline">
              Ver bandeja →
            </Link>
          </CardHeader>
          {emailsQ.isLoading ? (
            <p className="text-sm text-fg-tertiary py-3">Cargando…</p>
          ) : unreadEmails.length === 0 ? (
            <EmptyState padding="md" title="Bandeja al día" description="Ningún email sin leer." />
          ) : (
            <ul className="flex flex-col">
              {unreadEmails.map((email) => (
                <li
                  key={email.id}
                  className="flex items-start gap-2 py-2 border-b border-default last:border-b-0"
                >
                  <span
                    className="inline-block size-2 rounded-full shrink-0 mt-1.5"
                    style={{ backgroundColor: "var(--accent)" }}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-fg truncate">
                      {email.sender_name || email.sender_email}
                    </p>
                    <p className="text-sm text-fg-secondary truncate">{email.subject}</p>
                  </div>
                  <span className="text-xs text-fg-tertiary tabular font-mono shrink-0">
                    {formatRelativeTime(email.received_at, now)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* Row C */}
      <section className="grid grid-cols-12">
        <Card className="col-span-12" padding="md">
          <EmptyState
            icon={Bike}
            title="Conecta Strava"
            description="Cuando conectes una cuenta veremos aquí tus salidas, mileage semanal y trends."
            cta={{
              label: "Conectar Strava",
              onClick: () => alert("Disponible en Fase 3"),
              disabled: true,
              disabledHint: "Disponible en Fase 3 (US-07)",
            }}
          />
        </Card>
      </section>

      <Sheet
        open={openInvoice !== null}
        onClose={() => setOpenInvoice(null)}
        width="md"
        title={openInvoice ? `Factura ${openInvoice.number}` : undefined}
        description={openInvoice?.client}
      >
        {openInvoice ? <InvoiceSheetBody invoice={openInvoice} /> : null}
      </Sheet>
    </div>
  );
}

// `formatCurrency` is kept available for future formatting needs in this
// route even though the current rendering goes through `<Amount>`.
void formatCurrency;

export const Route = createFileRoute("/_authenticated/")({
  component: HomePage,
});
